// Reports page — sidebar + selected report.
//
// URL shape: /reports                     → landing (no report selected)
//            /reports/<report-id>          → view the report
//            /reports/<report-id>/edit     → edit the report
//
// The hash routing is parsed in App.jsx; we receive the sub-path as a
// prop. View mode is the default; the Edit button on a built-in clones
// to the user's list first (built-ins stay pristine).

import { useState } from 'react';
import { I } from '../components/icons.jsx';
import { ReportSidebar } from '../components/reports/ReportSidebar.jsx';
import { ReportView } from '../components/reports/ReportView.jsx';
import { ReportEdit } from '../components/reports/ReportEdit.jsx';
import {
  listReports, getReport,
  duplicateReport, deleteReport, blankReport, saveReport,
} from '../data/reports-store.js';

function ReportsLanding({ reports, onSelect, onNew }) {
  return (
    <div className="report-landing">
      <h2>Reports</h2>
      <p>
        Multi-panel snapshots of cluster state and your data, built from
        SQL. Pick a built-in to get started, duplicate one to customize,
        or start a blank report.
      </p>
      <div className="report-landing-grid">
        {reports.map(r => (
          <button
            key={r.id}
            type="button"
            className="k-card report-landing-card"
            onClick={() => onSelect(r.id)}
          >
            <div className="k-card-body">
              <div className="report-landing-head">
                <I.Reports size={18} />
                {r.builtin
                  ? <span className="pill cyan">Built-in</span>
                  : <span className="pill blue">Saved</span>}
              </div>
              <div className="report-landing-name">{r.name}</div>
              <div className="report-landing-desc">{r.description || `${r.panels.length} panel(s)`}</div>
            </div>
          </button>
        ))}
        <button
          type="button"
          className="k-card report-landing-card report-landing-new"
          onClick={onNew}
          title="Start a blank report"
        >
          <div className="k-card-body">
            <div className="report-landing-head">
              <I.Plus size={18} />
            </div>
            <div className="report-landing-name">New report</div>
            <div className="report-landing-desc">Start blank, add panels as you go.</div>
          </div>
        </button>
      </div>
    </div>
  );
}

export function ReportsPage({ sub, onNavigate }) {
  // Sub looks like '<id>' or '<id>/edit'. Empty = landing.
  const segs = (sub || '').split('/').filter(Boolean);
  const selectedId = segs[0] || '';
  const editing = segs[1] === 'edit';

  // Bump to force re-list after save/delete (since listReports reads
  // localStorage synchronously on each render).
  const [tick, setTick] = useState(0);
  const reload = () => setTick(t => t + 1);

  const reports = listReports(); // re-runs on `tick`
  // Read `tick` so React tracks the dependency and re-renders when it changes.
  void tick;

  const selected = selectedId ? getReport(selectedId) : null;

  const goLanding = () => onNavigate?.('reports', '');
  const goView    = (id) => onNavigate?.('reports', id);
  const goEdit    = (id) => onNavigate?.('reports', `${id}/edit`);

  // Edit a built-in: clone to user list first, then open the clone.
  const startEdit = () => {
    if (!selected) return;
    if (selected.builtin) {
      const copy = duplicateReport(selected.id);
      reload();
      goEdit(copy.id);
    } else {
      goEdit(selected.id);
    }
  };

  const startDuplicate = () => {
    if (!selected) return;
    const copy = duplicateReport(selected.id);
    reload();
    goView(copy.id);
  };

  const startDelete = () => {
    if (!selected || selected.builtin) return;
    if (!window.confirm(`Delete report "${selected.name}"? This can't be undone.`)) return;
    deleteReport(selected.id);
    reload();
    goLanding();
  };

  const startNew = () => {
    const fresh = saveReport(blankReport());
    reload();
    goEdit(fresh.id);
  };

  const onSaved = (savedReport) => {
    reload();
    goView(savedReport.id);
  };
  const onDiscard = () => goView(selectedId);

  // Header actions vary by mode.
  let headerActions = null;
  if (selected && !editing) {
    headerActions = (
      <>
        <button className="btn btn-ghost btn-sm" onClick={startDuplicate} title="Duplicate to your reports">
          <I.Save size={12} /> Duplicate
        </button>
        <button className="btn btn-primary btn-sm" onClick={startEdit} title={selected.builtin ? 'Clone first, then edit' : 'Edit this report'}>
          <I.Edit size={12} /> Edit
        </button>
      </>
    );
  } else if (selected && editing && !selected.builtin) {
    headerActions = (
      <button className="btn btn-ghost btn-sm" onClick={startDelete} title="Delete this report">
        <I.Close size={12} /> Delete
      </button>
    );
  } else if (!selected) {
    headerActions = (
      <button className="btn btn-primary btn-sm" onClick={startNew} title="Start a blank report">
        <I.Plus size={12} /> New report
      </button>
    );
  }

  return (
    <div className="page report-page">
      <div className="page-head">
        <div>
          <div className="crumbs">
            <button type="button" className="crumb-link" onClick={goLanding}>Reports</button>
            {selected && <> · <span>{selected.name}</span></>}
            {editing && <> · <span className="report-edit-crumb">editing</span></>}
          </div>
          <h1>{selected ? selected.name : 'Reports'}</h1>
        </div>
        <div className="actions">{headerActions}</div>
      </div>
      <div className="report-body">
        <ReportSidebar reports={reports} selectedId={selectedId} onSelect={goView} />
        <div className="report-main">
          {selected && editing ? (
            <ReportEdit report={selected} onSaved={onSaved} onDiscard={onDiscard} />
          ) : selected ? (
            <ReportView key={selected.id} report={selected} />
          ) : selectedId ? (
            <div className="report-not-found">
              No report with id <code>{selectedId}</code>.
              {' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={goLanding}>Back to Reports</button>
            </div>
          ) : (
            <ReportsLanding reports={reports} onSelect={goView} onNew={startNew} />
          )}
        </div>
      </div>
    </div>
  );
}
